import React, { useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { X, Upload, AlertTriangle, CheckCircle2, FileText } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Lead } from '../../types/lead.types';
import { CSV_IMPORT_TEMPLATES } from '../../config/csvTemplates';
import { leadImportService, ImportPreview } from '../../services/leadImport.service';
import { useAuth } from '../../contexts/AuthContext';

interface LeadImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  leads: Lead[];          // the current project's loaded leads
  onApplied: () => void;  // refresh after applying
}

/**
 * Upload a client recap CSV → match rows to existing leads → preview the field
 * changes → apply with an update-history entry per lead. Template-driven via
 * CSV_IMPORT_TEMPLATES so new clients are just new templates.
 */
export const LeadImportModal: React.FC<LeadImportModalProps> = ({ isOpen, onClose, leads, onApplied }) => {
  const { currentUser, userProfile } = useAuth();
  const [templateId, setTemplateId] = useState(CSV_IMPORT_TEMPLATES[0]?.id || '');
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [selections, setSelections] = useState<Map<number, string>>(new Map());
  const [applying, setApplying] = useState(false);
  const [fileName, setFileName] = useState('');

  const leadsById = useMemo(() => new Map(leads.map(l => [l.id, l])), [leads]);
  // All project leads, sorted, for manually mapping an unmatched recap row.
  const allLeadsSorted = useMemo(
    () => [...leads].sort((a, b) => (a.parentName || '').localeCompare(b.parentName || '')),
    [leads]
  );
  const template = CSV_IMPORT_TEMPLATES.find(t => t.id === templateId) || CSV_IMPORT_TEMPLATES[0];

  const reset = () => { setPreview(null); setSelections(new Map()); setFileName(''); };

  const handleFile = async (file: File) => {
    if (!template) return;
    try {
      // Accept the client's native Excel export (.xlsx/.xls) as well as CSV —
      // convert the first sheet to CSV text, then run the same parser.
      let text: string;
      if (/\.xlsx?$/i.test(file.name)) {
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        text = XLSX.utils.sheet_to_csv(ws);
      } else {
        text = await file.text();
      }
      const result = leadImportService.parseAndMatch(text, template, leads);
      setFileName(file.name);
      setPreview(result);
      // Pre-select all confidently-matched rows.
      const sel = new Map<number, string>();
      result.results.forEach((r, i) => { if (r.status === 'matched' && r.leadId) sel.set(i, r.leadId); });
      setSelections(sel);
    } catch (err) {
      console.error('Failed to parse CSV:', err);
      toast.error('Could not read that file. Make sure it is a CSV export.');
    }
  };

  const setSelection = (index: number, leadId: string) => {
    setSelections(prev => {
      const next = new Map(prev);
      if (leadId) next.set(index, leadId); else next.delete(index);
      return next;
    });
  };

  const handleApply = async () => {
    if (!preview) return;
    setApplying(true);
    try {
      const batchId = `recap-${Date.now()}`;
      const actor = currentUser
        ? { userId: currentUser.uid, userName: userProfile?.name || userProfile?.email || 'Recap import' }
        : undefined;
      const summary = await leadImportService.applyUpdates(preview, leadsById, selections, batchId, actor);
      toast.success(`Updated ${summary.updated} lead${summary.updated === 1 ? '' : 's'}` +
        (summary.noChange ? `, ${summary.noChange} unchanged` : '') +
        (summary.protectedValid ? `, ${summary.protectedValid} kept Valid (client rule)` : '') +
        (summary.failed ? `, ${summary.failed} failed` : ''));
      onApplied();
      reset();
      onClose();
    } catch (err) {
      console.error('Import apply failed:', err);
      toast.error('Failed to apply updates');
    } finally {
      setApplying(false);
    }
  };

  if (!isOpen) return null;

  const counts = preview ? {
    matched: preview.results.filter(r => r.status === 'matched').length,
    ambiguous: preview.results.filter(r => r.status === 'ambiguous').length,
    unmatched: preview.results.filter(r => r.status === 'unmatched').length,
  } : null;

  const willApply = Array.from(selections.values()).filter(Boolean).length;
  // Leads already claimed by another row (auto-matched or manually picked) —
  // hidden from the manual picker so you can't double-assign one parent.
  const usedLeadIds = new Set(Array.from(selections.values()).filter(Boolean));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-600" />
            <h2 className="text-lg font-semibold">Import lead recap (CSV)</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 overflow-y-auto">
          {/* Template + upload */}
          <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-4">
            {CSV_IMPORT_TEMPLATES.length > 1 && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Format</label>
                <select
                  value={templateId}
                  onChange={(e) => { setTemplateId(e.target.value); reset(); }}
                  className="px-3 py-2 border rounded-md text-sm"
                >
                  {CSV_IMPORT_TEMPLATES.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            )}
            <label className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium cursor-pointer hover:bg-indigo-700">
              <Upload className="w-4 h-4" />
              {fileName || 'Choose CSV file'}
              <input
                type="file"
                accept=".csv,text/csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }}
              />
            </label>
            <span className="text-xs text-gray-500">{leads.length} leads loaded in this project to match against.</span>
          </div>

          {/* Header errors */}
          {preview && preview.headerErrors.length > 0 && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                This file is missing required columns: <strong>{preview.headerErrors.join(', ')}</strong>.
                Check you uploaded the right export ({template?.name}).
              </div>
            </div>
          )}

          {/* Summary */}
          {counts && (
            <div className="flex flex-wrap gap-2 mb-3 text-xs">
              <span className="px-2 py-1 rounded-full bg-green-100 text-green-800">{counts.matched} matched</span>
              {counts.ambiguous > 0 && <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-800">{counts.ambiguous} need a pick</span>}
              {counts.unmatched > 0 && <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-700">{counts.unmatched} unmatched</span>}
              {preview!.skippedBlank > 0 && <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-500">{preview!.skippedBlank} blank skipped</span>}
            </div>
          )}

          {/* Results table */}
          {preview && preview.headerErrors.length === 0 && (
            <div className="border rounded-lg overflow-hidden">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Recap row</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Match</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Changes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {preview.results.map((r, i) => {
                    const name = `${r.parsed.raw['First Name'] || ''} ${r.parsed.raw['Last Name'] || ''}`.trim() || '(no name)';
                    return (
                      <tr key={i} className={r.status === 'unmatched' ? 'bg-gray-50/50' : ''}>
                        <td className="px-3 py-2 align-top">
                          <div className="font-medium text-gray-900">{name}</div>
                          <div className="text-xs text-gray-400">row {r.parsed.rowNumber}</div>
                        </td>
                        <td className="px-3 py-2 align-top">
                          {r.status === 'matched' && (
                            <span className="inline-flex items-center gap-1 text-green-700 text-xs">
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              {leadsById.get(r.leadId!)?.parentName || 'matched'}
                              <span className="text-gray-400">({r.matchedBy})</span>
                            </span>
                          )}
                          {r.status === 'ambiguous' && (
                            <select
                              value={selections.get(i) || ''}
                              onChange={(e) => setSelection(i, e.target.value)}
                              className="px-2 py-1 border rounded text-xs"
                            >
                              <option value="">Skip — pick a lead…</option>
                              {r.candidateLeadIds.map(id => (
                                <option key={id} value={id}>{leadsById.get(id)?.parentName} — {leadsById.get(id)?.phone}</option>
                              ))}
                            </select>
                          )}
                          {r.status === 'unmatched' && (
                            <div>
                              <select
                                value={selections.get(i) || ''}
                                onChange={(e) => setSelection(i, e.target.value)}
                                className="px-2 py-1 border rounded text-xs max-w-[220px]"
                              >
                                <option value="">No auto-match — pick a lead…</option>
                                {allLeadsSorted
                                  .filter(l => !usedLeadIds.has(l.id) || selections.get(i) === l.id)
                                  .map(l => (
                                    <option key={l.id} value={l.id}>
                                      {l.parentName || '(no name)'}{l.phone ? ` — ${l.phone}` : ''}
                                    </option>
                                  ))}
                              </select>
                              {!selections.get(i) && (
                                <div className="text-[11px] text-gray-400 mt-0.5">Skipped unless you choose a lead.</div>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 align-top">
                          {selections.get(i) ? (
                            <div className="flex flex-wrap gap-1">
                              {r.parsed.display.map((d, di) => (
                                <span key={di} className="text-xs px-1.5 py-0.5 bg-indigo-50 text-indigo-700 rounded">
                                  {d.field}: {d.value}
                                </span>
                              ))}
                              {r.protectedValid && (
                                <span className="text-xs px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded" title="Once a client export marks a lead Valid it stays valid — this row's downgrade is ignored.">
                                  kept Valid — downgrade ignored
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-300">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="p-4 border-t flex items-center justify-between bg-gray-50">
          <span className="text-sm text-gray-600">
            {preview ? `${willApply} lead${willApply === 1 ? '' : 's'} will be updated` : 'Upload a file to begin'}
          </span>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
            <button
              onClick={handleApply}
              disabled={!preview || willApply === 0 || applying || (preview?.headerErrors.length || 0) > 0}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {applying ? 'Applying…' : `Apply ${willApply} update${willApply === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
